import type {Location} from '@malt/nuxt-common-components-module/types';
import {getDefaultCitiesSuggestionsForLocale, defaultCitiesSuggestions} from '@malt/location-autocomplete/get-default-cities-suggestions';
import {useLocale, useUniversalFetch} from '#imports';

type BuildOptions = {
    remoteLabel?: string | null;
    globalMatching: boolean;
    countryCode?: string | null;
    layers?: string[];
};

async function resolveCountryLabelInternal(code: string): Promise<string> {
    try {
        const currentLocale = useLocale();
        const regionNames = new Intl.DisplayNames(currentLocale.languageCode.value, {type: 'region'});
        return regionNames.of(code) ?? code;
    } catch (e) {
        try {
            const country = await useUniversalFetch<{code: string; label: string}>(`/api/countries/${code}`);
            return country.label;
        } catch (e2) {
            return code;
        }
    }
}

export async function buildDefaultLocationSuggestions(options: BuildOptions): Promise<Location[]> {
    const result: Location[] = [];

    if (options.remoteLabel && options.remoteLabel !== '') {
        result.push({formattedAddress: options.remoteLabel, remoteAllowed: true});
    }

    if (options.globalMatching) {
        result.push(...defaultCitiesSuggestions.globalMatching);
    } else {
        const {locale} = useLocale();
        result.push(...getDefaultCitiesSuggestionsForLocale(locale.value));
    }

    if (options.countryCode && options.layers?.includes('COUNTRY')) {
        const label = await resolveCountryLabelInternal(options.countryCode);
        result.push({
            country: label,
            countryCode: options.countryCode,
            formattedAddress: label,
        });
    }

    return result;
}
